import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../../Header/auth - context';
import { ChatSocketContext } from '../../context/ChatSocketContext';
import useVisinaTabele from "../../custom/visinaTabele";
import './servis.css';

/* Prijave kvarova koje su vozači poslali sa telefona. Do sada se to nije videlo
   nigde na webu — vozač prijavi kvar, a operater o tome sazna usmeno. Ovde se
   vidi šta je prijavljeno, filtrira se po vozilu, i na svaku prijavu se
   evidentira šta je po njoj urađeno.

   Ono što je vozač uneo (opis, kategorija, fotografija) se NE menja — to je
   njegov iskaz o kvaru i ostaje kao trag. Operater dopisuje svoj deo. */

const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + 'Z';
};

const PrijaveKvarova = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector((state) => state.company);
    const toast = useRef(null);

    const [prijave, setPrijave] = useState(null);
    const [vozila, setVozila] = useState([]);
    const [statusi, setStatusi] = useState([]);
    const [majstori, setMajstori] = useState([]);

    const [filterVozilo, setFilterVozilo] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);

    const [izabrana, setIzabrana] = useState(null);
    const [forma, setForma] = useState({});
    const [cuva, setCuva] = useState(false);
    const [slikaUrl, setSlikaUrl] = useState(null);
    /* Spisak se zavrsava na dnu ekrana; visina se MERI, ne pogadja. */
    const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([]);

    const backend = process.env.REACT_APP_BACKEND_URL;

    const ucitaj = React.useCallback(() => {
        fetch(backend + '/service/faultReports', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({}),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((d) => {
                setPrijave(d.prijave || []);
                setVozila(d.vozila || []);
                setStatusi(d.statusi || []);
                setMajstori(d.majstori || []);
            })
            .catch(() => {
                setPrijave([]);
                toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: t('kvarovi.greskaUcitavanja'), life: 4000 });
            });
    }, [backend, auth.token, t]);

    useEffect(() => { ucitaj(); }, [ucitaj]);

    /* Nova prijava sa telefona, ili majstor/vozač završi posao — lista se osveži
       sama (server javi celoj firmi 'servis:prijava'). */
    const history = useHistory();
    const { socket } = useContext(ChatSocketContext) || {};
    useEffect(() => {
        if (!socket) return undefined;
        socket.on('servis:prijava', ucitaj);
        return () => socket.off('servis:prijava', ucitaj);
    }, [socket, ucitaj]);

    /* Završen posao se zavodi u istoriju servisa kroz „Unos servisa", sa već
       izabranim ovim nalogom — samo se proveri i sačuva (MB #146). */
    const otvoriUnosServisa = (red) => history.push({
        pathname: '/servis/unos_servisa',
        state: { selectedServiceLogId: red.ID },
    });

    const slikeRada = (red) => {
        try { const n = JSON.parse(red?.fotografijeRada || '[]'); return Array.isArray(n) ? n : []; } catch (e) { return []; }
    };
    const osnova = backend.replace(/\/API\/?$/, '');
    const jeZavrsen = (red) => String(red?.StatusOpis || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === 'zavrsen';
    const lokacijaTekst = (red) => {
        const m = red.GpsPreMinuta;
        if (m == null) return '';
        if (m < 2) return t('kvarovi.gpsUpravo');
        if (m < 60) return t('kvarovi.gpsPreMin', { n: m });
        if (m < 1440) return t('kvarovi.gpsPreSati', { n: Math.floor(m / 60) });
        return red.GpsVremeDisplay || '';
    };

    const prikazane = useMemo(() => {
        let r = prijave || [];
        if (filterVozilo) r = r.filter((x) => String(x.id_vozilo) === String(filterVozilo));
        if (filterStatus) r = r.filter((x) => String(x.statusServisa) === String(filterStatus));
        return r;
    }, [prijave, filterVozilo, filterStatus]);

    const otvori = (red) => {
        setIzabrana(red);
        setForma({
            spisakRadova: red.spisakRadova || '',
            napomena: red.napomena || '',
            statusServisa: red.statusServisa ?? null,
            id_zaposleni_majstor: red.id_zaposleni_majstor ?? null,
            kilometraza: red.kilometraza ?? '',
            datumZavrsetka: red.datumZavrsetka ? new Date(red.datumZavrsetka) : null,
        });
    };

    const sacuvaj = () => {
        if (!izabrana) return;
        setCuva(true);
        fetch(backend + '/service/faultWork', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({
                id: izabrana.ID,
                spisakRadova: forma.spisakRadova?.trim() || null,
                napomena: forma.napomena?.trim() || null,
                statusServisa: forma.statusServisa || null,
                id_zaposleni_majstor: forma.id_zaposleni_majstor || null,
                kilometraza: forma.kilometraza === '' ? null : Number(forma.kilometraza),
                datumZavrsetka: toUtcIso(forma.datumZavrsetka),
            }),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((d) => {
                toast.current?.show({ severity: 'success', summary: t('common.uspeh'), detail: d.message, life: 3000 });
                setIzabrana(null);
                ucitaj();
            })
            .catch(async (r) => {
                let poruka = t('kvarovi.greskaCuvanja');
                try { poruka = (await r.json()).message || poruka; } catch (e) { /* telo nije JSON */ }
                toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: poruka, life: 4000 });
            })
            .finally(() => setCuva(false));
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('PrijaveKvarova');
        const headers = [
            t('kvarovi.kolPrijavljeno'), t('kvarovi.kolVozilo'), t('kvarovi.kolPrijavio'),
            t('kvarovi.kolKategorija'), t('kvarovi.kolOpisVozaca'), t('kvarovi.kolStatus'),
            t('kvarovi.kolMajstor'), t('kvarovi.kolUradjeno'), t('kvarovi.kolZavrseno'),
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        prikazane.forEach((r) => {
            const row = worksheet.addRow([
                r.PrijavljenoDisplay, r.Registracija, r.PrijavioIme, r.KategorijaOpis,
                r.napomenaVozaca, r.StatusOpis, r.MajstorIme, r.spisakRadova, r.ZavrsenoDisplay,
            ]);
            row.eachCell((cell) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, String(cell.value).length); });
            col.width = Math.min(max + 2, 60);
        });
        const dt = new Date().toLocaleString('sr-RS', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
        }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            `${t('kvarovi.naslov')} - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const header = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                <Button type="button" icon="pi pi-refresh" severity="secondary" rounded onClick={ucitaj} data-pr-tooltip={t('common.osvezi')} />
            </div>
            <Dropdown
                value={filterVozilo}
                options={vozila.map((v) => ({ label: v.Registracija, value: v.ID }))}
                onChange={(e) => setFilterVozilo(e.value)}
                placeholder={t('kvarovi.filterVozilo')}
                showClear
                filter
                className="kvarovi-filter"
            />
            <Dropdown
                value={filterStatus}
                options={statusi.map((s) => ({ label: s.opis, value: s.id }))}
                onChange={(e) => setFilterStatus(e.value)}
                placeholder={t('kvarovi.filterStatus')}
                showClear
                className="kvarovi-filter"
            />
            <input
                type="search"
                onInput={(e) => setGlobalFilter(e.target.value)}
                placeholder={t('nova.phPretraga')}
                className="p-inputtext p-component"
            />
        </div>
    );

    /* Fotografija je često ceo sadržaj prijave — vozač slika kvar umesto da ga
       opisuje. Zato ide kao vidljivo dugme, ne kao sakriven link. */
    const teloFoto = (red) => (red.fotografija
        ? <Button type="button" icon="pi pi-image" rounded text onClick={() => setSlikaUrl(backend.replace(/\/API\/?$/, '') + red.fotografija)} />
        : <span className="kvarovi-prazno">—</span>);

    const teloStatus = (red) => (
        <span className={'kvarovi-status ' + (jeZavrsen(red) ? 'kvarovi-status-ok'
            : red.StatusOpis === 'U radionici' ? 'kvarovi-status-rad' : 'kvarovi-status-nov')}>
            {red.StatusOpis || '—'}
        </span>
    );

    const teloUradjeno = (red) => (red.spisakRadova
        ? <span className="kvarovi-uradjeno">{red.spisakRadova}</span>
        : <span className="kvarovi-prazno">{t('kvarovi.nijeEvidentirano')}</span>);

    return (
        <div className="zaposleni_table_container">
            <Toast ref={toast} />
            <Tooltip target=".export-buttons>button" position="bottom" />

            <div ref={omotTabele}>
            <DataTable
                className="stavke-tabela"
                value={prikazane}
                paginator
                rows={redovaTabele}
                header={header}
                globalFilter={globalFilter}
                scrollable
                scrollHeight={visinaTabele}
                emptyMessage={t('kvarovi.nemaPrijava')}
                loading={prijave === null}
                resizableColumns
                columnResizeMode="expand"
                rowClassName={(r) => (izabrana && izabrana.ID === r.ID ? 'selected-row' : '')}
            >
                <Column header={t('kvarovi.kolOtvori')} style={{ minWidth: '110px' }}
                    body={(r) => (
                        <Button label={t('kvarovi.evidentiraj')} icon="pi pi-pencil" type="button"
                            className="p-button-text p-button-sm" onClick={() => otvori(r)} />
                    )} />
                <Column field="PrijavljenoDisplay" header={t('kvarovi.kolPrijavljeno')} sortable style={{ minWidth: '150px' }} />
                <Column field="Registracija" header={t('kvarovi.kolVozilo')} sortable style={{ minWidth: '120px' }} />
                <Column field="PrijavioIme" header={t('kvarovi.kolPrijavio')} sortable style={{ minWidth: '150px' }} />
                <Column field="KategorijaOpis" header={t('kvarovi.kolKategorija')} sortable style={{ minWidth: '150px' }}
                    body={(r) => r.KategorijaOpis || '—'} />
                <Column field="napomenaVozaca" header={t('kvarovi.kolOpisVozaca')} style={{ minWidth: '240px' }}
                    body={(r) => (
                        <>
                            {r.napomenaVozaca || '—'}
                            {r.trebovanje && <div className="kvarovi-trebovanje-mini"><i className="pi pi-box" /> {r.trebovanje}</div>}
                        </>
                    )} />
                <Column header={t('kvarovi.kolFoto')} style={{ minWidth: '80px' }} body={teloFoto} />
                <Column field="StatusOpis" header={t('kvarovi.kolStatus')} sortable style={{ minWidth: '150px' }} body={teloStatus} />
                <Column field="MajstorIme" header={t('kvarovi.kolMajstor')} sortable style={{ minWidth: '150px' }}
                    body={(r) => r.MajstorIme || '—'} />
                <Column field="spisakRadova" header={t('kvarovi.kolUradjeno')} style={{ minWidth: '240px' }} body={teloUradjeno} />
                <Column field="ZavrsenoDisplay" header={t('kvarovi.kolZavrseno')} sortable style={{ minWidth: '120px' }}
                    body={(r) => r.ZavrsenoDisplay || '—'} />
            </DataTable>
            </div>

            <Dialog
                header={izabrana ? `${t('kvarovi.evidencijaNaslov')} — ${izabrana.Registracija || ''}` : ''}
                visible={!!izabrana}
                style={{ width: '46rem', maxWidth: '96vw' }}
                onHide={() => setIzabrana(null)}
            >
                {izabrana && (
                    <div className="kvarovi-forma">
                        {/* Šta je vozač prijavio — samo za čitanje, to se ne menja. */}
                        <div className="kvarovi-iskaz">
                            <div className="kvarovi-iskaz-naslov">{t('kvarovi.prijavioVozac')}</div>
                            <div className="kvarovi-iskaz-meta">
                                {izabrana.PrijavioIme} · {izabrana.PrijavljenoDisplay}
                                {izabrana.KategorijaOpis ? ` · ${izabrana.KategorijaOpis}` : ''}
                            </div>
                            <div className="kvarovi-iskaz-tekst">{izabrana.napomenaVozaca || '—'}</div>
                            {izabrana.trebovanje && (
                                <div className="kvarovi-trebovanje">
                                    <span>{t('kvarovi.trebovanje')}:</span> {izabrana.trebovanje}
                                </div>
                            )}
                        </div>

                        {/* Gde je vozilo SADA — da se odmah vidi je li na parkingu ili na putu */}
                        <div className="kvarovi-lokacija">
                            <i className="pi pi-map-marker" />
                            {izabrana.GpsLat != null ? (
                                <>
                                    <span>{t('kvarovi.voziloJeSada')}</span>
                                    <a href={`https://www.google.com/maps?q=${izabrana.GpsLat},${izabrana.GpsLng}`}
                                        target="_blank" rel="noopener noreferrer">
                                        {Number(izabrana.GpsLat).toFixed(5)}, {Number(izabrana.GpsLng).toFixed(5)}
                                    </a>
                                    <span className="kvarovi-lokacija-meta">
                                        {lokacijaTekst(izabrana)}
                                        {Number(izabrana.GpsBrzina) > 3 ? ` · ${Math.round(izabrana.GpsBrzina)} km/h` : ` · ${t('kvarovi.stoji')}`}
                                    </span>
                                </>
                            ) : <span className="kvarovi-lokacija-meta">{t('kvarovi.nemaGps')}</span>}
                        </div>

                        {/* Šta je majstor/vozač uneo sa telefona po završetku */}
                        {(izabrana.ZavrsioIme || izabrana.zamenjeniDelovi || slikeRada(izabrana).length > 0) && (
                            <div className="kvarovi-iskaz kvarovi-rad">
                                <div className="kvarovi-iskaz-naslov">{t('kvarovi.radSaTelefona')}</div>
                                {izabrana.ZavrsioIme && (
                                    <div className="kvarovi-iskaz-meta">{izabrana.ZavrsioIme}{izabrana.ZavrsenoDisplay ? ` · ${izabrana.ZavrsenoDisplay}` : ''}</div>
                                )}
                                {izabrana.zamenjeniDelovi && (
                                    <div className="kvarovi-iskaz-tekst"><b>{t('kvarovi.zamenjeniDelovi')}:</b> {izabrana.zamenjeniDelovi}</div>
                                )}
                                {slikeRada(izabrana).length > 0 && (
                                    <div className="kvarovi-slike">
                                        {slikeRada(izabrana).map((p) => (
                                            <button key={p} type="button" className="kvarovi-slika" onClick={() => setSlikaUrl(osnova + p)}>
                                                <img src={osnova + p} alt={t('kvarovi.kolFoto')} />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <label>{t('kvarovi.kolUradjeno')}</label>
                        <InputTextarea rows={5} value={forma.spisakRadova}
                            onChange={(e) => setForma({ ...forma, spisakRadova: e.target.value })}
                            placeholder={t('kvarovi.phUradjeno')} autoResize />

                        <div className="kvarovi-red">
                            <div>
                                <label>{t('kvarovi.kolStatus')}</label>
                                <Dropdown value={forma.statusServisa}
                                    options={statusi.map((s) => ({ label: s.opis, value: s.id }))}
                                    onChange={(e) => setForma({ ...forma, statusServisa: e.value })}
                                    placeholder={t('kvarovi.filterStatus')} />
                            </div>
                            <div>
                                <label>{t('kvarovi.kolMajstor')}</label>
                                <Dropdown value={forma.id_zaposleni_majstor}
                                    options={majstori.map((m) => ({ label: m.Ime, value: m.ID }))}
                                    onChange={(e) => setForma({ ...forma, id_zaposleni_majstor: e.value })}
                                    placeholder={t('kvarovi.izaberiMajstora')} filter showClear />
                            </div>
                        </div>

                        <div className="kvarovi-red">
                            <div>
                                <label>{t('kvarovi.kolKilometraza')}</label>
                                <InputText value={forma.kilometraza} keyfilter="pint"
                                    onChange={(e) => setForma({ ...forma, kilometraza: e.target.value })} />
                            </div>
                            <div>
                                <label>{t('kvarovi.kolZavrseno')}</label>
                                <Calendar value={forma.datumZavrsetka} dateFormat="dd.mm.yy." showIcon showButtonBar
                                    onChange={(e) => setForma({ ...forma, datumZavrsetka: e.value })} />
                            </div>
                        </div>

                        <label>{t('kvarovi.kolNapomena')}</label>
                        <InputTextarea rows={3} value={forma.napomena}
                            onChange={(e) => setForma({ ...forma, napomena: e.target.value })} autoResize />

                        <div className="kvarovi-dugmad">
                            {jeZavrsen(izabrana) && (
                                <button type="button" className="btn btn-primary kvarovi-unos-servisa"
                                    onClick={() => otvoriUnosServisa(izabrana)}
                                    title={izabrana.tip_servisa ? t('kvarovi.vecZaveden') : t('kvarovi.unosServisaSavet')}>
                                    <i className="pi pi-wrench" /> {t('kvarovi.unosServisa')}
                                    {izabrana.tip_servisa ? <i className="pi pi-check" /> : null}
                                </button>
                            )}
                            <button type="button" className="btn btn-secondary" onClick={() => setIzabrana(null)}>
                                {t('common.otkazi')}
                            </button>
                            <button type="button" className="btn btn-success" onClick={sacuvaj} disabled={cuva}>
                                {cuva ? t('common.cuvanje') : t('common.sacuvaj')}
                            </button>
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog header={t('kvarovi.kolFoto')} visible={!!slikaUrl} style={{ width: '40rem', maxWidth: '96vw' }}
                onHide={() => setSlikaUrl(null)}>
                {slikaUrl && <img src={slikaUrl} alt={t('kvarovi.kolFoto')} style={{ width: '100%', borderRadius: 8 }} />}
            </Dialog>
        </div>
    );
};

export default PrijaveKvarova;
